const DEFAULT_CAMPAIGN_ID_KEYS = [
  'campaignId',
  'campaign_id',
  'src',
  'source',
  'utmSource',
  'utm_source',
];
const DEFAULT_CAMPAIGN_NUMBER_KEYS = ['campaignNumber', 'campaign_number'];
const LEGACY_FORM_DATA_KEY_MAP = Object.freeze({
  firstname: 'first_name',
  lastname: 'last_name',
  fullname: 'first_name',
  email: 'email',
  phonenumber: 'phone',
  phone_number: 'phone',
  phone: 'phone',
  zipcode: 'zip_code',
  zip_code: 'zip_code',
  postalcode: 'zip_code',
  comments: 'comments',
  personalizedmessage: 'comments',
});
export const normalizeMetaKey = (value = '') =>
  String(value)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .toLowerCase();

const sanitizeMetaValue = (value) => {
  if (value === undefined || value === null) return undefined;

  if (Array.isArray(value)) {
    const joinedValue = value
      .map((item) => sanitizeMetaValue(item))
      .filter(Boolean)
      .join(',');
    return joinedValue || undefined;
  }

  if (value instanceof URL) {
    return value.toString();
  }

  const normalizedValue = typeof value === 'string' ? value.trim() : String(value).trim();
  return normalizedValue || undefined;
};

const pickFirst = (source, keys = []) => {
  if (!source) return undefined;

  return keys.reduce((resolvedValue, key) => resolvedValue ?? source[key], undefined);
};

const normalizeMetaObject = (source) => {
  if (!source || typeof source !== 'object') return {};

  return Object.entries(source).reduce((accumulator, [key, value]) => {
    const normalizedKey = normalizeMetaKey(key);

    if (normalizedKey) {
      accumulator[normalizedKey] = value;
    }

    return accumulator;
  }, {});
};

const mergeMeta = (...sources) => {
  return sources.reduce((accumulator, source) => {
    if (!source || typeof source !== 'object') return accumulator;

    Object.entries(source).forEach(([key, value]) => {
      const sanitizedValue = sanitizeMetaValue(value);

      if (sanitizedValue !== undefined) {
        accumulator[key] = sanitizedValue;
      }
    });

    return accumulator;
  }, {});
};

export const createEventId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

const mapLegacyFormDataKeys = (formData = {}) => {
  const normalizedData = normalizeMetaObject(formData);

  return Object.entries(normalizedData).reduce((accumulator, [key, value]) => {
    accumulator[LEGACY_FORM_DATA_KEY_MAP[key] || key] = value;
    return accumulator;
  }, {});
};

export const toDigitsOnly = (value = '') => String(value).replace(/\D/g, '');

export const buildLegacyCampaignMeta = ({
  campaign = {},
  storedPhone,
  defaultCampaignNumber,
} = {}) => {
  const rawCampaignNumber = pickFirst(
    {
      campaignNumber: storedPhone,
      ...campaign,
    },
    DEFAULT_CAMPAIGN_NUMBER_KEYS,
  );
  const campaignNumber = toDigitsOnly(rawCampaignNumber);
  const normalizedDefaultCampaignNumber = toDigitsOnly(defaultCampaignNumber);
  const campaignId = pickFirst(campaign, DEFAULT_CAMPAIGN_ID_KEYS);

  return mergeMeta({
    campaign_number: sanitizeMetaValue(rawCampaignNumber),
    campaign_id: campaignId,
    source: campaignNumber
      ? campaignNumber === normalizedDefaultCampaignNumber
        ? 'organic'
        : 'paid'
      : undefined,
  });
};

export const buildLegacyFormMeta = ({
  formData = {},
  analytics = {},
  campaign = {},
  storedPhone,
  defaultCampaignNumber,
  formType,
  error,
  meta = {},
  eventId,
  includeFormData = true,
  includeAnalyticsMeta = false,
} = {}) => {
  return mergeMeta(
    includeFormData ? mapLegacyFormDataKeys(formData) : undefined,
    {
      placement: analytics.placement,
      form_type: formType,
      event_id: eventId || createEventId(),
      ...(error ? { error } : {}),
    },
    buildLegacyCampaignMeta({
      campaign,
      storedPhone,
      defaultCampaignNumber,
    }),
    includeAnalyticsMeta ? analytics.meta : undefined,
    meta,
  );
};

export const buildFormEventPayload = ({
  event,
  eventCategory,
  eventAction,
  eventLabel,
  eventValue,
  meta,
} = {}) => {
  const payload = {
    event,
    eventCategory,
    eventAction,
    eventLabel,
  };

  if (typeof eventValue === 'number' && Number.isFinite(eventValue)) {
    payload.eventValue = eventValue;
  }

  if (meta && Object.keys(meta).length > 0) {
    payload.meta = meta;
  }

  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined));
};
